import { createApiClient } from './client'

/**
 * Type definitions for Ranger AI Assistant
 */
export interface RangerMessage {
  role: 'user' | 'assistant'
  content: string
}

/** A focused tool the user picked from the chat's Tools menu. */
export type RangerTool = 'build_trip'

export interface RangerChatRequest {
  message: string
  tripId?: string
  sessionId?: string
  conversationHistory?: RangerMessage[]
  mode?: RangerTool
  /** The user's calendar date (YYYY-MM-DD), so relative dates resolve in their timezone. */
  localDate?: string
}

/** Result of Ranger's create_trip tool. */
export interface RangerBuiltTrip {
  id: string
  name: string
  start_date: string | null
  end_date: string | null
  destination: string | null
  destination_pinned: boolean
  description: string | null
  suggested_friend_ids: string[]
  unmatched_names: string[]
  ambiguous_names: Array<{ name: string; candidates: string[] }>
}

/** Result of Ranger's invite_friends tool. */
export interface RangerInviteFriendsResult {
  invited: string[]
  already_invited: string[]
  failed: string[]
  not_found: string[]
  ambiguous: Array<{ name: string; candidates: string[] }>
}

export interface RangerFunctionCallResult {
  name: string
  success: boolean
  result?: unknown
  error?: string
}

export interface RangerChatResponse {
  message: string
  sessionId?: string
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  metadata?: {
    tripContext?: boolean
    model?: string
    provider?: 'bedrock'
    functionCalls?: RangerFunctionCallResult[]
  }
}

export interface TripContext {
  tripId: string
  tripName: string
  startDate?: string
  endDate?: string
  description?: string
  waypoints?: Array<{
    name: string
    latitude?: number
    longitude?: number
    orderIndex: number
  }>
  crewCount?: number
  vehicles?: Array<{
    name: string
    make?: string
    model?: string
  }>
}

export interface GenerateIngredientsRequest {
  mealName: string
  tripId?: string
  mealType?: string
  servingSize?: number
}

export interface GenerateIngredientsResponse {
  ingredients: string[]
}

/** Result of adding meals from food names, from the next-steps card or Ranger's add_meals tool. */
export interface RangerAddedMeals {
  added: Array<{
    id: string
    name: string
    planned_date: string | null
    planned_meal_time: 'breakfast' | 'lunch' | 'dinner' | 'snack'
    ingredients?: string[]
  }>
  already_planned: string[]
}

export interface GenerateTripDescriptionRequest {
  sentence: string
  tripId?: string
  tripName?: string
  startDate?: string
  endDate?: string
}

export interface GenerateTripDescriptionResponse {
  description: string
}

/**
 * Ranger API client for interacting with Ranger AI Assistant
 */
export class RangerApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  /**
   * Send a chat message to Ranger
   * POST /api/v1/ranger/chat
   */
  async chat(request: RangerChatRequest): Promise<RangerChatResponse> {
    const response = await this.apiClient.post<{
      success: boolean
      data: RangerChatResponse
    }>('/ranger/chat', request)
    return response.data
  }

  /**
   * Get trip context for Ranger
   * GET /api/v1/ranger/trip/:tripId/context
   */
  async getTripContext(tripId: string): Promise<TripContext> {
    const response = await this.apiClient.get<{
      success: boolean
      data: TripContext
    }>(`/ranger/trip/${tripId}/context`)
    return response.data
  }

  /**
   * Generate ingredients for a meal
   * POST /api/v1/ranger/meals/generate-ingredients
   */
  async generateIngredients(request: GenerateIngredientsRequest): Promise<GenerateIngredientsResponse> {
    const response = await this.apiClient.post<{
      success: boolean
      data: GenerateIngredientsResponse
    }>('/ranger/meals/generate-ingredients', request)
    return response.data
  }

  /**
   * Add meals to a trip from a list of food names
   * POST /api/v1/ranger/trips/:tripId/meals
   */
  async addMealsFromFoods(tripId: string, foods: string): Promise<RangerAddedMeals> {
    const response = await this.apiClient.post<{
      success: boolean
      data: RangerAddedMeals
    }>(`/ranger/trips/${tripId}/meals`, { foods })
    return response.data
  }

  /**
   * Expand a one-sentence trip idea into a full description
   * POST /api/v1/ranger/trips/generate-description
   */
  async generateTripDescription(
    request: GenerateTripDescriptionRequest
  ): Promise<GenerateTripDescriptionResponse> {
    const response = await this.apiClient.post<{
      success: boolean
      data: GenerateTripDescriptionResponse
    }>('/ranger/trips/generate-description', request)
    return response.data
  }
}

